import { FC } from 'react';
import {
  ExpandMoreRounded,
  ArrowForwardRounded,
  BoltRounded,
  CalendarMonthRounded,
  SettingsRounded,
  ShieldRounded,
  TaskAltRounded,
} from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import './TeamSideNav.scss';
import { TeamDetailSection, TeamSettingsSection } from '../constants';

interface TeamSideNavProps {
  activeSection: TeamDetailSection;
  activeSettingsSection: TeamSettingsSection;
  sharedCalendarCount: number;
  sharedTaskBoardCount: number;
  shareEnergyEnabled: boolean;
  canRenameTeam: boolean;
  canManageTeamProjects: boolean;
  onNavigate: (section: TeamDetailSection, settingsSection?: TeamSettingsSection) => void;
}

const TeamSideNav: FC<TeamSideNavProps> = ({
  activeSection,
  activeSettingsSection,
  sharedCalendarCount,
  sharedTaskBoardCount,
  shareEnergyEnabled,
  canRenameTeam,
  canManageTeamProjects,
  onNavigate,
}) => {
  const { t } = useTranslation();
  const isSettingsActive = activeSection === 'settings';
  const settingItems: { id: TeamSettingsSection; label: string }[] = [
    ...(canRenameTeam ? [{ id: 'rename' as const, label: t('team.settings.rename.title') }] : []),
    { id: 'sharedCalendar', label: t('team.settings.sharedCalendar.title') },
    { id: 'sharedTodos', label: t('team.settings.sharedTodos.title') },
    { id: 'sharedEnergy', label: t('team.settings.sharedEnergy.title') },
    ...(canManageTeamProjects
      ? [{ id: 'projects' as const, label: t('team.projects.title') }]
      : []),
    { id: 'members', label: t('team.settings.members.title') },
    { id: 'delete', label: t('team.settings.delete.navTitle') },
  ];
  const resolvedActiveSettingsSection = settingItems.some(
    (settingItem) => settingItem.id === activeSettingsSection
  )
    ? activeSettingsSection
    : settingItems[0].id;

  return (
    <aside className="team-side-nav" aria-label={t('team.sideNav.ariaSections')}>
      <div className="team-side-nav__menu-panel">
        <nav className="team-side-nav__items">
          <button
            type="button"
            className={`team-side-nav__item${
              activeSection === 'day' ? ' team-side-nav__item--active' : ''
            }`}
            onClick={() => onNavigate('day')}
          >
            <CalendarMonthRounded className="team-side-nav__icon" />
            <Typography variant="body2" component="span" className="team-side-nav__item-title">
              {t('team.sideNav.teamDay')}
            </Typography>
          </button>

          <button
            type="button"
            className={`team-side-nav__item${
              activeSection === 'tasks' ? ' team-side-nav__item--active' : ''
            }`}
            onClick={() => onNavigate('tasks')}
          >
            <TaskAltRounded className="team-side-nav__icon" />
            <Typography variant="body2" component="span" className="team-side-nav__item-title">
              {t('team.sideNav.teamTasks')}
            </Typography>
          </button>

          <Accordion
            disableGutters
            elevation={0}
            expanded={isSettingsActive}
            className="team-side-nav__accordion"
            onChange={(_, expanded) => {
              if (expanded) {
                onNavigate('settings', resolvedActiveSettingsSection);
                return;
              }

              onNavigate('day');
            }}
          >
            <AccordionSummary
              className={`team-side-nav__item${isSettingsActive ? ' team-side-nav__item--active' : ''}`}
              expandIcon={<ExpandMoreRounded className="team-side-nav__accordion-chevron" />}
            >
              <div className="team-side-nav__item-left">
                <SettingsRounded className="team-side-nav__icon" />
                <Typography variant="body2" component="span" className="team-side-nav__item-title">
                  {t('team.sideNav.settings')}
                </Typography>
              </div>
            </AccordionSummary>

            <AccordionDetails className="team-side-nav__sub-sections">
              {settingItems.map((settingItem) => (
                <button
                  key={settingItem.id}
                  type="button"
                  className={`team-side-nav__sub-item${
                    resolvedActiveSettingsSection === settingItem.id
                      ? ' team-side-nav__sub-item--active'
                      : ''
                  }`}
                  onClick={() => onNavigate('settings', settingItem.id)}
                >
                  <Typography
                    variant="body2"
                    component="span"
                    className="team-side-nav__sub-item-title"
                  >
                    {settingItem.label}
                  </Typography>
                </button>
              ))}
            </AccordionDetails>
          </Accordion>
        </nav>

        <div className="team-side-nav__sharing-card">
          <div className="team-side-nav__sharing-card-title">
            <ShieldRounded className="team-side-nav__sharing-card-icon" />
            <Typography variant="body2" component="span">
              {t('team.sideNav.yourSharing')}
            </Typography>
          </div>
          <div className="team-side-nav__sharing-card-copy">
            <Typography variant="subtitle2" component="span">
              <CalendarMonthRounded />{' '}
              {t('team.sideNav.calendarShared', { count: sharedCalendarCount })}
            </Typography>
            <Typography variant="subtitle2" component="span">
              <TaskAltRounded /> {t('team.sideNav.tasksShared', { count: sharedTaskBoardCount })}
            </Typography>
            <Typography variant="subtitle2" component="span">
              <BoltRounded />{' '}
              {t('team.sideNav.energyStatus', {
                status: shareEnergyEnabled ? t('team.common.on') : t('team.common.off'),
              })}
            </Typography>
          </div>
          <button
            type="button"
            className="team-side-nav__sharing-card-link"
            onClick={() => onNavigate('settings', 'sharedCalendar')}
          >
            <Typography variant="subtitle2" component="span">
              {t('team.sideNav.reviewSettings')}
            </Typography>
            <ArrowForwardRounded />
          </button>
        </div>
      </div>
    </aside>
  );
};

export default TeamSideNav;
